<template>
  <v-stepper-vertical-item
    :value="0"
    :elevation="0"
    :complete="currentStep > 0"
    :class="{
      'step-completed': currentStep > 0,
      'step-error': step1HasErrors,
      'hide-step': currentStep > 4,
    }"
  >
    <template #title>
      <div class="subtitle1 step-title">
        {{ $t("modals.allowCloudService.step0.title") }}
        <template v-if="currentStep > 0"
          >:
          <span class="body2 text-indigo-medium ml-2">
            {{ isOtherProvider ? modelValue.domain : $t("services." + modelValue.selectedService) }}
          </span>
        </template>
      </div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > 0" icon="$check" />
      <span v-else-if="step1HasErrors">!</span>
      <span v-else>1</span>
    </template>
    <div class="step1">
      <p class="body2 mb-4">
        {{ $t("modals.allowCloudService.step0.description") }}
      </p>

      <v-chip-group
        class="justify-center mt-4 mb-6"
        :model-value="modelValue.selectedService"
        :disabled="hasExistingConnection"
        @update:modelValue="onSelectedServiceChange"
      >
        <v-chip v-for="item in availableServices" :key="item" :value="item" variant="outlined">
          <div
            class="d-flex flex-column align-center w-100 provider-selection-chip"
            :data-testid="`${item}-chip`"
          >
            <coro-icon v-if="item !== 'other'" class="service-icon" :icon-name="item"></coro-icon>
            <v-icon v-else size="48" icon="$cube"></v-icon>
            <div class="subtitle1">
              {{
                item !== "other"
                  ? $t(`cloudApplications.${item}`)
                  : $t(`modals.allowCloudService.step0.otherEmailService`)
              }}
            </div>
          </div>
        </v-chip>
      </v-chip-group>

      <div v-if="modelValue.selectedService === 'other'">
        <v-text-field
          data-testid="add-admin-modal-lastname"
          class="mt-6 mb-2"
          variant="outlined"
          :rules="domainRules"
          :label="$t('modals.allowCloudService.step0.domain')"
          :aria-label="$t('modals.allowCloudService.step0.domain')"
          :placeholder="$t('modals.allowCloudService.step0.domain')"
          :model-value="modelValue.domain"
          @update:modelValue="onDomainChange"
        ></v-text-field>
        <v-alert :closable="false" icon="icon-warning-xs" class="mt-5 info-alert">
          <div class="subtitle1">
            {{ $t(`modals.allowCloudService.step0.otherEmailAlertTitle`) }}
          </div>
          <ul class="body2">
            <li>{{ $t("modals.allowCloudService.step0.otherEmailAlertLine1") }}</li>
            <li>{{ $t("modals.allowCloudService.step0.otherEmailAlertLine2") }}</li>
          </ul>
        </v-alert>
      </div>

      <v-alert
        v-if="selectedServiceNotConnected"
        :closable="false"
        color="red-faint"
        icon="icon-warning-xs"
        class="mt-5 text-red-dark"
      >
        <div class="body2 text-red-dark">
          {{
            $t(`modals.allowCloudService.step0.connectionRequired`, {
              serviceName: $t("services." + modelValue.selectedService),
            })
          }}
        </div>
        <div
          @click="redirectToAppsPage"
          class="text-red-dark cursor-pointer subtitle2 text-decoration-underline"
        >
          {{
            $t("modals.allowCloudService.step0.connectService", {
              serviceName: $t("services." + modelValue.selectedService),
            })
          }}
        </div>
      </v-alert>

      <div class="mt-5">
        <v-btn
          color="primary"
          rounded
          :disabled="step1HasErrors"
          :loading="loading"
          @click="moveToNextStep"
        >
          {{ $t("modals.allowCloudService.step0.actionBtn") }}
        </v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { Service, ServiceStatus } from "@/constants/cloud-apps.ts";
import { useI18n } from "vue-i18n";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
  isSATCustomDomain,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import CoroIcon from "@/components/CoroIcon.vue";
import Patterns from "@/constants/patterns.ts";
import { storeToRefs } from "pinia";
import { RouteName } from "@/constants/routes.ts";
import { useRouter } from "vue-router";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";

export default defineComponent({
  components: { CoroIcon },
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
    hasExistingConnection: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue", "next", "dismissed"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const securityAwarenessStore = useSecurityAwarenessModule();
    const cloudAppsStore = useCloudAppsStore();
    const router = useRouter();
    const { services } = storeToRefs(cloudAppsStore);
    const { satConnections } = storeToRefs(securityAwarenessStore);
    const loading = ref(false);

    const formValue = computed(() => props.modelValue);

    const updateLocalValue = (partial: Partial<SatConnectionConfigForm>) => {
      emit("update:modelValue", { ...props.modelValue, ...partial });
    };

    const availableServices = computed(() => {
      return [Service.G_SUITE, Service.OFFICE_365, "other"].filter((svc) => {
        const isSelected = formValue.value.selectedService === svc;
        return (
          !satConnections.value.some(({ name }) =>
            svc === "other" ? isSATCustomDomain(name) : name === svc
          ) || isSelected
        );
      });
    });

    const domainRules = [
      (domain: string) => {
        if (!domain || domain.length <= 0) {
          return i18n.t("validations.required");
        }
        if (!Patterns.PUNYCODE_DOMAIN.test(domain.trim())) {
          return i18n.t("validations.punycodeDomain");
        }
        if (Patterns.NOT_ALLOWED_DOMAIN_ZONE.test(domain.trim())) {
          return i18n.t("validations.domainZoneIsNotAllowed");
        }

        return true;
      },
    ];

    const selectedServiceNotConnected = computed(() => {
      const serviceObj = services.value.find(
        (service) => service.name === formValue.value.selectedService
      );

      if (serviceObj) {
        return ![ServiceStatus.CONNECTED, ServiceStatus.CONNECTED_NOT_SECURE].includes(
          serviceObj.serviceStatus
        );
      }

      return false;
    });

    const isOtherProvider = computed(() => {
      return formValue.value.selectedService === "other";
    });

    const step1HasErrors = computed(() => {
      const isDomainValid = domainRules.every((rule) => rule(formValue.value.domain) === true);

      return (
        (isOtherProvider.value && !isDomainValid) ||
        selectedServiceNotConnected.value ||
        !formValue.value.selectedService
      );
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const moveToNextStep = async () => {
      loading.value = true;
      const config =
        formValue.value.selectedService === "other"
          ? {
              status: SatConfigurationStatus.SELECTED,
              customDomain: formValue.value.domain,
            }
          : {
              status: SatConfigurationStatus.SELECTED,
            };

      await securityAwarenessStore.updateSatConfig(config, satConfigurationProvider.value);
      loading.value = false;
      emit("next");
    };

    const redirectToAppsPage = () => {
      router.push({ name: RouteName.CLOUD_APPS_PAGE });
      emit("dismissed");
    };

    const onSelectedServiceChange = (value: string | Service) => {
      updateLocalValue({ selectedService: value });
    };

    const onDomainChange = (value: string) => {
      updateLocalValue({ domain: value });
    };

    return {
      availableServices,
      domainRules,
      step1HasErrors,
      selectedServiceNotConnected,
      isOtherProvider,
      moveToNextStep,
      redirectToAppsPage,
      onSelectedServiceChange,
      onDomainChange,
      loading,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  height: 48px;
  width: 48px;
}

.step1 {
  .v-chip {
    width: 200px;
    height: 160px !important;
    border-radius: 6px;
    background-color: rgb(var(--v-theme-white));
    border: 0;
    justify-content: center;

    .v-chip__content {
      width: 100% !important;
      text-align: center;
    }
  }

  .v-chip:not(.v-chip--selected) {
    border: solid 1px rgb(var(--v-theme-indigo-pale));
  }

  .v-chip-group .v-chip--selected {
    border: 2px solid rgb(var(--v-theme-primary)) !important;
  }
}

.info-alert {
  background-color: rgb(var(--v-theme-indigo-pale));
}

ul {
  list-style-type: disc !important;
}

.text-red-dark::before {
  color: rgb(var(--v-theme-red-dark));
}
</style>
